import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import bgGif from "@/bg.gif";
import { Instagram, Play } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { ResultAsync, errAsync } from "neverthrow";

type AlbumArtSlideshowProps = {
  className?: string;
};

type HeroAlbumsResponse = {
  urls: string[];
};

const CANVAS_WIDTH = 64;
const CANVAS_HEIGHT = 64;
const SLIDESHOW_INTERVAL_MS = 3000;

/*
 * Fisher–Yates shuffle copy.
 * Inputs: args.items.
 * Outputs: New array with the same elements in random order.
 */
const shuffleItems = <T,>(args: { items: readonly T[] }): T[] => {
  const out = [...args.items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
};

/*
 * Fetches the list of static hero album image URLs from the API.
 * Inputs: none.
 * Outputs: ResultAsync of { urls }.
 */
const fetchHeroAlbumUrls = () =>
  ResultAsync.fromPromise(
    fetch("/api/marketing/hero-albums"),
    (error) => ({ code: "HERO_ALBUMS_LIST_FETCH_FAILED" as const, error })
  ).andThen((response) => {
    if (!response.ok) {
      return errAsync({
        code: "HERO_ALBUMS_LIST_HTTP_ERROR" as const,
        error: new Error(`Request failed with status ${response.status}`),
        status: response.status,
      });
    }

    return ResultAsync.fromPromise(
      response.json() as Promise<HeroAlbumsResponse>,
      (error) => ({ code: "HERO_ALBUMS_LIST_JSON_FAILED" as const, error })
    );
  });

/*
 * Picks a representative RGB from RGBA image data for the glow (same brightness gate as before).
 * Inputs: args.imageData - full canvas snapshot (e.g. 64×64).
 * Outputs: CSS rgb(...) components as "r, g, b".
 */
const extractDominantColourFromImageData = (args: { imageData: ImageData }) => {
  const { imageData } = args;
  const d = imageData.data;
  const pixelCount = imageData.width * imageData.height;
  let totalR = 0;
  let totalG = 0;
  let totalB = 0;
  let samples = 0;
  const sampleStep = 8;

  for (let p = 0; p < pixelCount; p += sampleStep) {
    const i = p * 4;
    const r = d[i] ?? 0;
    const g = d[i + 1] ?? 0;
    const b = d[i + 2] ?? 0;
    const brightness = r + g + b;
    if (brightness > 50 && brightness < 700) {
      totalR += r;
      totalG += g;
      totalB += b;
      samples += 1;
    }
  }

  if (samples === 0) {
    return "139, 92, 246";
  }

  const avgR = Math.round(totalR / samples);
  const avgG = Math.round(totalG / samples);
  const avgB = Math.round(totalB / samples);

  return `${avgR}, ${avgG}, ${avgB}`;
};

/*
 * Slideshow of PNGs from /hero-albums; preloads the frame after next so transitions stay instant.
 * Inputs: props.className.
 * Outputs: Canvas plus border wrapper.
 */
const AlbumArtSlideshow = (props: AlbumArtSlideshowProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const cacheRef = useRef<Map<string, ImageBitmap>>(new Map());
  const urlsRef = useRef<string[]>([]);
  const [orderedUrls, setOrderedUrls] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [glowColour, setGlowColour] = useState<string | null>(null);
  const [decodeTick, setDecodeTick] = useState(0);

  const preloadBitmap = useCallback(async (args: { url: string }) => {
    const { url } = args;
    if (cacheRef.current.has(url)) {
      return;
    }
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error("Hero album image request failed", { url, status: response.status });
        return;
      }
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);
      cacheRef.current.set(url, bitmap);
      setDecodeTick((n) => n + 1);
    } catch (error) {
      console.error("Hero album image decode failed", { url, error });
    }
  }, []);

  useEffect(() => {
    return () => {
      for (const bitmap of cacheRef.current.values()) {
        bitmap.close();
      }
      cacheRef.current.clear();
    };
  }, []);

  useEffect(() => {
    void fetchHeroAlbumUrls()
      .map((data) => {
        if (data.urls.length === 0) {
          setIsLoading(false);
          return;
        }

        const shuffled = shuffleItems({ items: data.urls });
        urlsRef.current = shuffled;
        setOrderedUrls(shuffled);

        void (async () => {
          try {
            await preloadBitmap({ url: shuffled[0]! });
            if (shuffled.length > 1) {
              void preloadBitmap({ url: shuffled[1]! });
            }
          } finally {
            setIsLoading(false);
          }
        })();
      })
      .mapErr((err) => {
        console.error("Failed to fetch hero album list", err);
        setIsLoading(false);
      });
  }, [preloadBitmap]);

  useEffect(() => {
    if (orderedUrls.length <= 1) {
      return undefined;
    }

    const id = window.setInterval(() => {
      setCurrentIndex((prev) => {
        const urls = urlsRef.current;
        if (urls.length === 0) {
          return prev;
        }
        const next = (prev + 1) % urls.length;
        const warmIdx = (next + 1) % urls.length;
        const warmUrl = urls[warmIdx];
        if (warmUrl) {
          void preloadBitmap({ url: warmUrl });
        }
        return next;
      });
    }, SLIDESHOW_INTERVAL_MS);

    return () => window.clearInterval(id);
  }, [orderedUrls.length, preloadBitmap]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || orderedUrls.length === 0) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const url = orderedUrls[currentIndex];
    if (!url) {
      return;
    }

    const bitmap = cacheRef.current.get(url);
    if (!bitmap) {
      void preloadBitmap({ url });
      return;
    }

    ctx.imageSmoothingEnabled = false;
    canvas.style.imageRendering = "pixelated";
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    ctx.drawImage(bitmap, 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    const imageData = ctx.getImageData(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    setGlowColour(extractDominantColourFromImageData({ imageData }));
  }, [orderedUrls, currentIndex, decodeTick, preloadBitmap]);

  return (
    <div className={cn("border-4 border-white/20", props.className)}>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="h-[256px] w-[256px] saturate-[3] brightness-110 contrast-110 transition-all duration-300"
        style={{
          imageRendering: "pixelated",
          boxShadow: glowColour ? `0 0 40px rgba(${glowColour}, 1), 0 0 80px rgba(${glowColour}, 0.3)` : undefined,
          filter: "blur(0px)",
        }}
        aria-busy={isLoading}
        aria-label={isLoading ? "Loading album artwork preview" : "Album artwork preview"}
      />
    </div>
  );
};

/*
 * Fixed full-screen animated background GIF behind all home page content.
 * A dark base layer sits behind the image; a CSS mask fades the GIF to transparent towards the bottom.
 * Inputs: none.
 * Outputs: Fixed background layer covering the viewport.
 */
export const BackgroundGif = () => {
  return (
    <div className="pointer-events-none fixed inset-0 z-10" aria-hidden>
      <div className="absolute inset-0 bg-background" />
      <img
        src={bgGif}
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-40"
        style={{
          imageRendering: "pixelated",
          maskImage: "linear-gradient(to bottom, black 0%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, black 0%, transparent 100%)",
        }}
      />
    </div>
  );
};

const INSTAGRAM_TILEE_URL = "https://www.instagram.com/tilee.io/";

/*
 * Sticky top navigation with home link, FAQs, and Instagram.
 * Inputs: none.
 * Outputs: A translucent navigation bar that adapts when the user scrolls.
 */
export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 48);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <nav
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        isScrolled
          ? "border-b border-border/80 bg-background/85 backdrop-blur"
          : "border-b border-transparent bg-background/40 backdrop-blur-sm",
      )}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link
          to="/"
          className="font-serif text-2xl lowercase tracking-tight text-foreground transition-opacity hover:opacity-80"
        >
          tilee
        </Link>
        <div className="flex items-center gap-6 text-sm">
          <Link to="/faqs" className="text-muted-foreground transition-colors hover:text-foreground">
            FAQs
          </Link>
          <a
            href={INSTAGRAM_TILEE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-muted-foreground transition-colors hover:text-foreground"
            aria-label="Tilee on Instagram"
          >
            <Instagram className="size-5" aria-hidden />
          </a>
        </div>
      </div>
    </nav>
  );
};

/*
 * Minimal hero: headline, short line, album preview, primary buy action.
 * Inputs: none.
 * Outputs: Centred full-viewport hero section.
 */
export const HeroSection = () => {
  return (
    <section className="relative flex min-h-screen flex-col items-center justify-center px-6 pb-16 pt-20 text-center sm:pb-24 sm:pt-28">
      <div className="mx-auto flex max-w-4xl flex-col items-center gap-3 sm:gap-5">
        <h1 className="font-serif text-5xl tracking-tight text-foreground sm:text-6xl md:text-7xl">
          Your music,
          <br />
          illuminated
        </h1>
        <p className="max-w-xl text-balance text-base text-muted-foreground md:text-lg">
          The square LED matrix that connects to your Spotify
        </p>
      </div>
      <div className="mt-8 flex w-full max-w-3xl justify-center sm:mt-12">
        <AlbumArtSlideshow />
      </div>
      <div className="mt-8 flex flex-col items-center gap-4 sm:mt-12 sm:flex-row">
        <Button className="border border-foreground/30 bg-black text-foreground hover:bg-white/10" size="lg">
          <Play className="size-4 fill-current" aria-hidden />
          Watch video
        </Button>
        <Button className="border border-muted-foreground bg-foreground text-background hover:bg-foreground/90" size="lg">
          Buy now for €49
        </Button>
      </div>
      <p className="mt-3 text-xs text-muted-foreground opacity-60">Shipping calculated at checkout</p>
    </section>
  );
};
